import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useModuleDetailsQuery } from '../api/studentCoursesApi';
import { tokenService } from '../services/TokenService';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  ArrowLeft,
  Clock,
  Target,
  CheckCircle2,
  PlayCircle,
  ExternalLink,
  RotateCcw,
  Loader2,
  AlertCircle
} from 'lucide-react';
import QuizContainer from '../components/modules/quiz/QuizContainer';
import routes from '../routes';

export default function QuizLandingPage() {
  const { contextId, moduleId } = useParams<{ contextId: string; moduleId: string }>();
  const navigate = useNavigate();
  const token = tokenService.getToken();
  
  const [showQuizFrame, setShowQuizFrame] = useState(false);
  const [useNativeQuiz, setUseNativeQuiz] = useState(true);

  // Fetch quiz details including attempts
  const { 
    data: quiz, 
    isLoading, 
    error,
    refetch 
  } = useModuleDetailsQuery(
    contextId || '',
    moduleId || '',
    'quiz',
    token
  );

  const handleBack = () => {
    navigate(routes.toCourseDetails(contextId || ''));
  };

  const handleStartQuiz = () => {
    setShowQuizFrame(true);
  };

  const handleCloseQuiz = () => {
    setShowQuizFrame(false);
    // Refetch to update attempts after quiz
    refetch();
  };

  const formatDuration = (seconds: number | undefined) => {
    if (!seconds) return 'No limit';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} mins`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  // If showing quiz frame (taking the quiz)
  if (showQuizFrame && contextId && quiz?.id) {
    return (
      <QuizContainer
        contextId={contextId || ''}
        quizId={quiz.id}
        quizName={quiz.name || 'Quiz'}
        onClose={handleCloseQuiz}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
        <Loader2 className="h-10 w-10 animate-spin text-blue-600 mb-4" />
        <p className="text-gray-600 font-medium">Loading quiz details...</p>
      </div>
    );
  }

  if (error || !quiz) {
    return (
      <div className="p-8 max-w-4xl mx-auto">
        <Button variant="ghost" onClick={handleBack} className="mb-6 pl-0 hover:pl-0 hover:bg-transparent text-gray-600 hover:text-gray-900">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Course
        </Button>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 flex items-center gap-4 text-red-800">
          <AlertCircle className="h-8 w-8 text-red-600 flex-shrink-0" />
          <div>
            <h3 className="font-semibold text-lg mb-1">Error Loading Quiz</h3>
            <p className="text-sm opacity-90">Failed to load quiz details. Please try again later.</p>
          </div>
        </div>
      </div>
    );
  }

  const isQuizOpen = () => {
    const now = new Date();
    if (quiz.timeOpen && new Date(quiz.timeOpen) > now) return false;
    if (quiz.timeClose && new Date(quiz.timeClose) < now) return false;
    return true;
  };

  const quizOpen = isQuizOpen();

  return (
    <div className="min-h-screen bg-gray-50 p-6 md:p-10 font-sans">
      <div className="max-w-5xl mx-auto space-y-8">
        
        {/* Header */}
        <div>
          <Button variant="ghost" onClick={handleBack} className="mb-4 pl-0 -ml-2 text-gray-500 hover:text-gray-900">
            <ArrowLeft className="h-4 w-4 mr-1.5" />
            Back to CourseContent
          </Button>
          
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-orange-100 rounded-lg text-orange-600">
                  <Target className="h-6 w-6" />
                </div>
                <h1 className="text-3xl font-bold text-gray-900">{quiz.name}</h1>
              </div>
              <p className="text-gray-500">Quiz Assessment</p>
            </div>
            
            {/* <Badge variant={quizOpen ? 'default' : 'secondary'} className={`px-4 py-1.5 text-sm ${quizOpen ? 'bg-green-600 hover:bg-green-700' : ''}`}>
              {quizOpen ? 'Open for Attempts' : 'Closed'}
            </Badge> */}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content - Description & Info */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="border-none shadow-sm bg-white overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-gray-50 to-white border-b border-gray-100 pb-4">
                <CardTitle className="text-lg flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-blue-600" />
                  Instructions
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                {quiz.intro ? (
                  <div 
                    className="prose prose-sm max-w-none text-gray-700 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: quiz.intro }} 
                  />
                ) : (
                  <p className="text-gray-500 italic">No specific instructions for this quiz.</p>
                )}
              </CardContent>
            </Card>

            {/* Attempts History */}
            <Card className="border-none shadow-sm bg-white overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-gray-50 to-white border-b border-gray-100 pb-4">
                <CardTitle className="text-lg flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <RotateCcw className="h-5 w-5 text-blue-600" />
                    Summary of your previous attempts
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {quiz.attemptsList && quiz.attemptsList.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50/50 hover:bg-gray-50/50 border-gray-100">
                        <TableHead className="w-20 font-semibold">Attempt</TableHead>
                        <TableHead className="font-semibold">State</TableHead>
                        <TableHead className="font-semibold hidden md:table-cell">Started On</TableHead>
                        <TableHead className="font-semibold hidden md:table-cell">Completed On</TableHead>
                        <TableHead className="text-right font-semibold">Grade / {quiz.grade}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {quiz.attemptsList.map((attempt: any) => (
                        <TableRow key={attempt.id} className="border-gray-50 hover:bg-gray-50/50">
                          <TableCell className="font-medium text-center">{attempt.attempt}</TableCell>
                          <TableCell>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize
                              ${attempt.state === 'finished' ? 'bg-green-100 text-green-800' : 
                                attempt.state === 'inprogress' ? 'bg-blue-100 text-blue-800' : 
                                'bg-gray-100 text-gray-800'}`}>
                              {attempt.state.replace('_', ' ')}
                            </span>
                          </TableCell>
                          <TableCell className="text-muted-foreground text-xs hidden md:table-cell">
                            {formatDate(attempt.timestart ? new Date(attempt.timestart * 1000).toISOString() : '')}
                          </TableCell>
                          <TableCell className="text-muted-foreground text-xs hidden md:table-cell">
                           {attempt.timefinish > 0 
                              ? formatDate(new Date(attempt.timefinish * 1000).toISOString())
                              : '-'}
                          </TableCell>
                          <TableCell className="text-right font-semibold text-gray-900">
                            {attempt.sumgrades !== undefined && attempt.sumgrades !== null 
                              ? Number(attempt.sumgrades).toFixed(2) 
                              : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-10 px-4">
                    <p className="text-gray-500 text-sm">You haven't attempted this quiz yet.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar - Quiz Meta & Actions */}
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-6">
              <h3 className="font-semibold text-gray-900 border-b border-gray-100 pb-3">Quiz Details</h3>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2 text-gray-600">
                    <Clock className="h-4 w-4 text-blue-500" />
                    <span>Time Limit</span>
                  </div>
                  <span className="font-medium text-gray-900">{formatDuration(quiz.timeLimit)}</span>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2 text-gray-600">
                    <RotateCcw className="h-4 w-4 text-orange-500" />
                    <span>Attempts Allowed</span>
                  </div>
                  <span className="font-medium text-gray-900">
                    {quiz.attempts === 0 ? 'Unlimited' : quiz.attempts}
                  </span>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2 text-gray-600">
                    <Target className="h-4 w-4 text-purple-500" />
                    <span>Grading Method</span>
                  </div>
                  <span className="font-medium text-gray-900 capitalize">Highest Grade</span>
                </div>
              </div>

              <div className="pt-2">
                <Button 
                  onClick={handleStartQuiz} 
                  disabled={!quizOpen}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white shadow-md transition-all hover:shadow-lg h-11"
                >
                  <PlayCircle className="h-5 w-5 mr-2" />
                  {quiz.attemptsList?.some((a: any) => a.state === 'inprogress') ? 'Continue Attempt' : 'Attempt Quiz Now'}
                </Button>
                
                {contextId && (
                  <div className="flex items-center justify-center gap-2 mt-4 pt-4 border-t border-gray-100">
                    <input
                      type="checkbox"
                      id="native-quiz"
                      checked={useNativeQuiz}
                      onChange={(e) => setUseNativeQuiz(e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 cursor-pointer"
                    />
                    <label htmlFor="native-quiz" className="text-xs font-medium text-gray-600 cursor-pointer select-none">
                      Use Native Quiz Experience
                    </label>
                  </div>
                )}
              </div>
            </div>

            {/* Availability Info */}
            {(quiz.timeOpen || quiz.timeClose) && (
               <div className="bg-blue-50/50 rounded-xl border border-blue-100 p-5 space-y-3">
                 <h4 className="text-sm font-semibold text-blue-900">Availability</h4>
                 <div className="space-y-2 text-sm">
                   {quiz.timeOpen && (
                     <div className="flex justify-between">
                       <span className="text-blue-700/70">Opens</span>
                       <span className="font-medium text-blue-900">{formatDate(quiz.timeOpen.toString())}</span>
                     </div>
                   )}
                   {quiz.timeClose && (
                     <div className="flex justify-between">
                       <span className="text-blue-700/70">Closes</span>
                       <span className="font-medium text-blue-900">{formatDate(quiz.timeClose.toString())}</span>
                     </div>
                   )}
                 </div>
               </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
